import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { apiPrivate, API_BASE, getAccessToken } from "@/lib/api";
import { History, Filter, Download, Loader2, Activity } from "lucide-react";

const ACTION_LABELS = {
    create_socio: { label: "Creazione socio", color: "emerald" },
    update_socio: { label: "Modifica socio", color: "magenta" },
    deactivate_socio: { label: "Disattivazione socio", color: "vermillion" },
    send_communication: { label: "Invio comunicazione", color: "bayside" },
    deactivate_listing: { label: "Disattivazione inserzione", color: "vermillion" },
};

const colorMap = {
    emerald: "text-emerald-300 bg-emerald-500/10 border-emerald-400/30",
    magenta: "text-magenta bg-magenta/10 border-magenta/40",
    vermillion: "text-vermillion bg-vermillion/10 border-vermillion/40",
    bayside: "text-bayside bg-bayside/10 border-bayside/40",
    zinc: "text-zinc-400 bg-white/5 border-white/10",
};

export default function AdminAudit() {
    const [entries, setEntries] = useState([]);
    const [stats, setStats] = useState(null);
    const [filters, setFilters] = useState({ action: "", date_from: "", date_to: "" });
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        const params = {};
        if (filters.action) params.action = filters.action;
        if (filters.date_from) params.date_from = filters.date_from;
        if (filters.date_to) params.date_to = filters.date_to;
        const [es, st] = await Promise.all([
            apiPrivate.get("/admin/audit", { params }),
            apiPrivate.get("/admin/audit/stats"),
        ]);
        setEntries(es.data || []);
        setStats(st.data);
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

    const downloadCsv = async () => {
        const params = new URLSearchParams();
        if (filters.action) params.set("action", filters.action);
        if (filters.date_from) params.set("date_from", filters.date_from);
        if (filters.date_to) params.set("date_to", filters.date_to);
        const url = `${API_BASE}/admin/audit/export.csv?${params.toString()}`;
        const r = await fetch(url, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
        const blob = await r.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
    };

    return (
        <AppLayout admin>
            <div className="max-w-[1400px] mx-auto" data-testid="admin-audit">
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <span className="label-tag text-magenta">Compliance</span>
                        <h1 className="font-display text-5xl mt-3 tracking-tighter">
                            Audit <span className="font-serif-italic text-gradient-tropical">trail</span>.
                        </h1>
                        <p className="text-zinc-400 mt-3 max-w-2xl">
                            Log immutabile di tutte le operazioni amministrative. Utile per revisioni legali, controlli interni e tracciabilità GDPR.
                        </p>
                    </div>
                    <button onClick={downloadCsv} className="btn-ghost" data-testid="audit-export-btn">
                        <Download className="h-4 w-4" /> Esporta CSV
                    </button>
                </div>

                {/* Stats grid */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8" data-testid="audit-stats">
                        <div className="bento p-4">
                            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">Eventi totali</div>
                            <div className="font-display text-3xl mt-1">{stats.total}</div>
                        </div>
                        {Object.entries(stats.by_action).slice(0, 3).map(([action, count]) => {
                            const meta = ACTION_LABELS[action] || { label: action, color: "zinc" };
                            return (
                                <div key={action} className="bento p-4">
                                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">{meta.label}</div>
                                    <div className="font-display text-3xl mt-1">{count}</div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Filters */}
                <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid="audit-filters">
                    <div>
                        <div className="label-tag mb-2"><Filter className="h-3 w-3 inline mr-1" /> Tipo evento</div>
                        <select value={filters.action} onChange={(e) => setFilters(f => ({ ...f, action: e.target.value }))} data-testid="audit-action-filter" className="w-full bg-[#0F0F0F] border border-white/10 text-white px-3 py-2 font-grotesk text-sm focus:outline-none focus:border-bayside rounded-xl">
                            <option value="">Tutti</option>
                            {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <div className="label-tag mb-2">Da</div>
                        <input type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))} data-testid="audit-from" className="w-full bg-[#0F0F0F] border border-white/10 text-white px-3 py-2 font-grotesk text-sm focus:outline-none focus:border-bayside rounded-xl" />
                    </div>
                    <div>
                        <div className="label-tag mb-2">A</div>
                        <input type="date" value={filters.date_to} onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))} data-testid="audit-to" className="w-full bg-[#0F0F0F] border border-white/10 text-white px-3 py-2 font-grotesk text-sm focus:outline-none focus:border-bayside rounded-xl" />
                    </div>
                    <button onClick={() => setFilters({ action: "", date_from: "", date_to: "" })} className="btn-ghost" data-testid="audit-reset">Reset filtri</button>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="bento p-12 text-center text-zinc-500 mt-6"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
                ) : entries.length === 0 ? (
                    <div className="bento p-12 text-center text-zinc-500 mt-6" data-testid="audit-empty">
                        <Activity className="h-10 w-10 mx-auto mb-3 opacity-40" />
                        Nessun evento corrisponde ai filtri.
                    </div>
                ) : (
                    <div className="bento p-0 overflow-hidden mt-6" data-testid="audit-table">
                        <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/10 text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">
                            <div className="col-span-3">Timestamp</div>
                            <div className="col-span-3">Azione</div>
                            <div className="col-span-3">Attore</div>
                            <div className="col-span-3">Target / Dettagli</div>
                        </div>
                        <div className="divide-y divide-white/5">
                            {entries.map(e => {
                                const meta = ACTION_LABELS[e.action] || { label: e.action, color: "zinc" };
                                return (
                                    <div key={e.id} className="grid grid-cols-12 gap-3 px-4 py-3 text-sm font-grotesk hover:bg-white/[0.02]" data-testid={`audit-row-${e.id}`}>
                                        <div className="col-span-3 text-zinc-400 text-xs font-mono">
                                            {new Date(e.timestamp).toLocaleString("it-IT")}
                                        </div>
                                        <div className="col-span-3">
                                            <span className={`text-[10px] uppercase tracking-widest font-grotesk px-2.5 py-1 rounded-full border ${colorMap[meta.color]}`}>{meta.label}</span>
                                        </div>
                                        <div className="col-span-3 text-white truncate">
                                            <div className="truncate">{e.actor_name || "—"}</div>
                                            <div className="text-xs text-zinc-500 truncate">{e.actor_email}</div>
                                        </div>
                                        <div className="col-span-3 text-zinc-300 text-xs truncate">
                                            {e.target_email && <div className="truncate">{e.target_email}</div>}
                                            {e.changes && e.changes.length > 0 && <div className="text-zinc-500">Campi: {e.changes.join(", ")}</div>}
                                            {e.metadata && Object.entries(e.metadata).map(([k, v]) => <div key={k} className="text-zinc-500 truncate">{k}: {String(v)}</div>)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </AppLayout>
    );
}
